import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { mockApi, emailIntents } from '../utils/mockApi';
import { 
  Users,
  Mail,
  Phone,
  Building,
  Calendar,
  Trash2,
  ExternalLink,
  Download
} from 'lucide-react';

const AdminLeads = () => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadLeads();
  }, []);

  const loadLeads = () => {
    setLoading(true);
    const result = mockApi.getLeads();
    if (result.success) {
      setLeads(result.data);
    }
    setLoading(false);
  };

  const clearAllLeads = () => {
    if (window.confirm('Are you sure you want to clear all leads? This action cannot be undone.')) {
      const result = mockApi.clearLeads();
      if (result.success) {
        setLeads([]);
        alert('All leads cleared successfully');
      }
    }
  };

  const exportLeads = () => {
    const dataStr = JSON.stringify(leads, null, 2);
    const dataBlob = new Blob([dataStr], {type: 'application/json'});
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `automats-leads-${new Date().toISOString().split('T')[0]}.json`;
    link.click();
  };

  const getUrgencyColor = (urgency) => {
    switch (urgency) {
      case 'urgent': return 'bg-red-500 text-white';
      case 'high': return 'bg-orange-500 text-white';
      case 'normal': return 'bg-gray-500 text-white';
      default: return 'bg-gray-500 text-white';
    }
  };

  const getInquiryTypeColor = (type) => {
    switch (type) {
      case 'quote': return 'text-automats-red';
      case 'dealer': return 'text-purple-600';
      case 'sample': return 'text-automats-eco-green';
      case 'support': return 'text-blue-600';
      default: return 'text-automats-steel';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-automats-red mx-auto"></div>
          <p className="mt-4 font-body text-automats-steel">Loading leads...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-heading font-bold text-automats-black">
                Lead Management
              </h1>
              <p className="font-body text-automats-steel mt-1">
                Demo admin panel - leads stored in localStorage
              </p>
            </div>
            <div className="flex items-center space-x-4">
              <Button 
                onClick={exportLeads}
                variant="outline"
                className="font-body"
                disabled={leads.length === 0}
              >
                <Download className="h-4 w-4 mr-2" />
                Export JSON
              </Button>
              <Button 
                onClick={clearAllLeads}
                variant="outline"
                className="font-body text-red-600 border-red-600 hover:bg-red-600 hover:text-white"
                disabled={leads.length === 0}
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Clear All
              </Button>
              <Button 
                onClick={loadLeads}
                className="bg-automats-red hover:bg-red-700 font-body"
              >
                Refresh
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center">
                <Users className="h-8 w-8 text-automats-red" />
                <div className="ml-4">
                  <div className="text-2xl font-heading font-bold text-automats-black">{leads.length}</div>
                  <div className="text-sm font-body text-automats-steel">Total Leads</div>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center">
                <Mail className="h-8 w-8 text-automats-eco-green" />
                <div className="ml-4">
                  <div className="text-2xl font-heading font-bold text-automats-black">
                    {leads.filter(l => l.inquiryType === 'quote' || l.inquiry_type === 'quote').length}
                  </div>
                  <div className="text-sm font-body text-automats-steel">Quote Requests</div>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center">
                <Building className="h-8 w-8 text-purple-600" />
                <div className="ml-4">
                  <div className="text-2xl font-heading font-bold text-automats-black">
                    {leads.filter(l => l.inquiryType === 'dealer' || l.inquiry_type === 'dealer').length}
                  </div>
                  <div className="text-sm font-body text-automats-steel">Dealer Inquiries</div>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center">
                <Calendar className="h-8 w-8 text-blue-600" />
                <div className="ml-4">
                  <div className="text-2xl font-heading font-bold text-automats-black">
                    {leads.filter(l => {
                      const leadDate = new Date(l.timestamp);
                      const today = new Date();
                      return leadDate.toDateString() === today.toDateString();
                    }).length}
                  </div>
                  <div className="text-sm font-body text-automats-steel">Today</div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Leads List */}
        {leads.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <Users className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-heading font-semibold text-gray-600 mb-2">No leads yet</h3>
              <p className="font-body text-gray-500">
                Leads submitted through the website forms will appear here.
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            {leads.map((lead) => (
              <Card key={lead.id} className="hover:shadow-lg transition-all duration-200">
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <CardTitle className="font-heading text-xl text-automats-black">
                        {lead.name}
                        {lead.company && (
                          <span className="text-base font-body text-automats-steel ml-2">
                            at {lead.company}
                          </span>
                        )}
                      </CardTitle>
                      <CardDescription className="font-body mt-1">
                        Submitted {new Date(lead.timestamp).toLocaleString()}
                      </CardDescription>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Badge 
                        className={`font-body ${getInquiryTypeColor(lead.inquiryType || lead.inquiry_type)}`}
                        variant="outline"
                      >
                        {(lead.inquiryType || lead.inquiry_type || 'general').toUpperCase()}
                      </Badge>
                      {lead.urgency && (
                        <Badge className={`font-body ${getUrgencyColor(lead.urgency)}`}>
                          {lead.urgency.toUpperCase()}
                        </Badge>
                      )}
                    </div>
                  </div>
                </CardHeader>
                
                <CardContent className="space-y-4">
                  {/* Contact Information */}
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="flex items-center space-x-2">
                      <Mail className="h-4 w-4 text-automats-steel" />
                      <span className="font-body text-automats-steel">{lead.email}</span>
                      <a 
                        href={`mailto:${lead.email}`}
                        className="text-automats-red hover:underline"
                      >
                        <ExternalLink className="h-3 w-3" />
                      </a>
                    </div>
                    {lead.phone && (
                      <div className="flex items-center space-x-2">
                        <Phone className="h-4 w-4 text-automats-steel" />
                        <span className="font-body text-automats-steel">{lead.phone}</span>
                        <a 
                          href={`tel:${lead.phone}`}
                          className="text-automats-red hover:underline"
                        >
                          <ExternalLink className="h-3 w-3" />
                        </a>
                      </div>
                    )}
                  </div>

                  {/* Message */}
                  <div>
                    <h4 className="font-body font-semibold text-automats-black mb-2">Message:</h4>
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <p className="font-body text-automats-steel whitespace-pre-wrap">
                        {lead.message}
                      </p>
                    </div>
                  </div>

                  {/* Email Intent */}
                  <div className="flex items-center justify-between pt-4 border-t">
                    <div className="flex items-center space-x-2">
                      <span className="font-body text-sm text-automats-steel">Route to:</span>
                      <Badge variant="outline" className="font-body">
                        {emailIntents[lead.inquiryType || lead.inquiry_type || 'general']}
                      </Badge>
                    </div>
                    
                    <div className="flex space-x-2">
                      <Button 
                        size="sm" 
                        variant="outline"
                        className="font-body"
                        onClick={() => window.open(`mailto:${lead.email}?subject=Re: Your AutoMats Inquiry`)}
                      >
                        Reply
                      </Button>
                      <Button 
                        size="sm" 
                        className="bg-automats-red hover:bg-red-700 font-body"
                        onClick={() => window.open(`tel:${lead.phone || ''}`)}
                        disabled={!lead.phone}
                      >
                        Call
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminLeads;